import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Copy,
  Download,
  FileText,
  Loader2,
  Mail,
  Users,
  Video,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/Components/ui/badge";
import { Button } from "@/Components/ui/button";
import { Input } from "@/Components/ui/input";
import { Label } from "@/Components/ui/label";
import { Textarea } from "@/Components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/Components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/Components/ui/dialog";
import { Text } from "@/Components/ui/typography";
import { cn } from "@/lib/utils";
import {
  cancelLiveMeetBooking,
  fetchLiveMeetTranscriptPdfUrl,
  openLiveMeetRoomFromBooking,
  proceedLiveMeetWithoutConfirmations,
  rescheduleLiveMeetBooking,
  sendLiveMeetConfirmationReminder,
} from "./liveMeetApi";
import InterviewerFeedbackSummary from "./InterviewerFeedbackSummary";
import {
  createClientRequestId,
  DEFAULT_MEETING_TIMEZONE,
  getTodayDateString,
  isValidIanaTimezone,
  MEETING_TIMEZONE_OPTIONS,
  normalizeTimeValue,
  toIsoInTimezone,
  validateRescheduleTimeWindow,
  validateRescheduleWithinAvailability,
} from "./meetingScheduleUtils";
import InterviewRescheduleSlotEditor, {
  buildRescheduleSlotsFromBooking,
  createRescheduleSlotDraft,
  suggestionToRescheduleDraft,
} from "./InterviewRescheduleSlotEditor";
import {
  getReminderEventLabel,
  getReminderStatusLabel,
  isReminderTimelineEvent,
  resolveReminderRecipientEmails,
  resolveReminderSenderEmail,
} from "./interviewRoomReminderDetails";
import {
  canViewInterviewRoomTranscript,
  formatInterviewRoomSchedule,
  getInterviewRoomEmailDelivery,
  getInterviewRoomEmailStatusBadgeClassName,
  getInterviewRoomJoinUrl,
  getInterviewRoomShareUrl,
  getInterviewRoomStatusBadgeClassName,
  getInterviewRoomStatusMessage,
  getJoinUnavailableAlertMessage,
  getInterviewRoomTypeLabel,
  getLiveInterviewRecordingListItemState,
  getRescheduleSuggestedSlots,
  isInstantLiveMeetBooking,
  isInterviewRoomReschedulePending,
  isLiveMeetBookingProvider,
  canSendLiveMeetConfirmationReminders,
  isLiveMeetRoomClosed,
  mergeLiveMeetBookingWithRoom,
  parseInterviewRoomDate,
  resolveInterviewRoomStatus,
} from "./interviewRoomBookingUtils";
import {
  getLiveInterviewRecordingDownloadUrl,
  getLiveInterviewRoom,
} from "../../../LiveInterview/api/liveInterviewApi";

const copyText = async (value) => {
  const text = String(value || "").trim();
  if (!text) {
    throw new Error("No meeting link is available yet.");
  }
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "readonly");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

const StatCard = ({ label, value, detail, icon: Icon }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
    <div className="flex items-center gap-2 text-slate-500">
      {Icon ? <Icon className="h-4 w-4" /> : null}
      <Text size="xs" className="font-semibold uppercase tracking-wide">
        {label}
      </Text>
    </div>
    <Text size="sm" className="mt-3 font-semibold text-slate-900">
      {value || "-"}
    </Text>
    {detail ? (
      <Text size="xs" className="mt-1 text-slate-500">
        {detail}
      </Text>
    ) : null}
  </div>
);

const formatInputDateInTimezone = (isoValue, timezone) => {
  if (!isoValue || !isValidIanaTimezone(timezone)) return "";
  try {
    const parsedDate = parseInterviewRoomDate(isoValue);
    if (!parsedDate) return "";
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat("en-CA", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
        .formatToParts(parsedDate)
        .map((part) => [part.type, part.value])
    );
    if (!parts.year || !parts.month || !parts.day) return "";
    return `${parts.year}-${parts.month}-${parts.day}`;
  } catch {
    return "";
  }
};

const formatInputTimeInTimezone = (isoValue, timezone) => {
  if (!isoValue || !isValidIanaTimezone(timezone)) return "";
  try {
    const parsedDate = parseInterviewRoomDate(isoValue);
    if (!parsedDate) return "";
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat("en-GB", {
        timeZone: timezone,
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      })
        .formatToParts(parsedDate)
        .map((part) => [part.type, part.value])
    );
    return normalizeTimeValue(`${parts.hour || ""}:${parts.minute || ""}`);
  } catch {
    return "";
  }
};

const formatTimelineTimestamp = (isoValue, timezone) => {
  const parsedDate = parseInterviewRoomDate(isoValue);
  if (!parsedDate) return "";
  const safeTimezone = isValidIanaTimezone(timezone)
    ? timezone
    : Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  try {
    return new Intl.DateTimeFormat("en-IN", {
      timeZone: safeTimezone,
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(parsedDate);
  } catch {
    return parsedDate.toLocaleString("en-IN");
  }
};

const formatConfirmationStatusDetail = (entry, timezone, pendingMessage) => {
  if (entry?.confirmed_at) {
    return `Confirmed at ${formatTimelineTimestamp(entry.confirmed_at, timezone)}`;
  }
  return pendingMessage;
};

const getTimelineEventBadgeClassName = (eventType) => {
  switch (String(eventType || "").trim().toLowerCase()) {
    case "created":
      return "border-sky-200 bg-sky-50 text-sky-700";
    case "rescheduled":
      return "border-violet-200 bg-violet-50 text-violet-700";
    case "link_expired":
      return "border-amber-200 bg-amber-50 text-amber-700";
    case "email_success":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "email_partial":
      return "border-orange-200 bg-orange-50 text-orange-700";
    case "email_failed":
      return "border-rose-200 bg-rose-50 text-rose-700";
    case "cancelled":
    case "declined":
      return "border-rose-200 bg-rose-50 text-rose-700";
    case "completed":
      return "border-slate-200 bg-slate-100 text-slate-700";
    case "booking_confirmed":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "confirmation_reminder_sent":
      return "border-sky-200 bg-sky-50 text-sky-700";
    case "host_confirmed_without_all_confirmations":
      return "border-amber-200 bg-amber-50 text-amber-700";
    default:
      return "border-slate-200 bg-slate-50 text-slate-600";
  }
};

const getTimelineEventLabel = (eventType) => {
  switch (String(eventType || "").trim().toLowerCase()) {
    case "created":
      return "Created";
    case "rescheduled":
      return "Rescheduled";
    case "reschedule_requested":
      return "Reschedule requested";
    case "link_expired":
      return "Link Retired";
    case "email_success":
      return "Emails Sent";
    case "email_partial":
      return "Email Issue";
    case "email_failed":
      return "Email Failed";
    case "cancelled":
      return "Cancelled";
    case "declined":
      return "Declined";
    case "completed":
      return "Completed";
    case "booking_confirmed":
      return "Confirmed";
    case "confirmation_reminder_sent":
      return "Reminder sent";
    case "host_confirmed_without_all_confirmations":
      return "Host override";
    case "interview_reminder_sent":
      return "Reminder sent";
    case "interviewer_feedback_submitted":
      return "Feedback";
    case "interviewer_feedback_deferred":
      return "Feedback deferred";
    default:
      return "Update";
  }
};

const getTimelineChipLabel = (event) => {
  const eventType = String(event?.event_type || "").trim().toLowerCase();
  if (eventType === "booking_confirmed") {
    const title = String(event?.title || "").trim();
    if (title) return title;
  }
  return getTimelineEventLabel(eventType);
};

const normalizeTimelineEmail = (value) => String(value || "").trim().toLowerCase();

const resolveTimelineActorRole = (event, booking) => {
  const storedRole = String(
    event?.event_data?.actor || event?.event_data?.role || ""
  )
    .trim()
    .toLowerCase();
  if (
    storedRole === "candidate" ||
    storedRole === "interviewer" ||
    storedRole === "host"
  ) {
    return storedRole;
  }

  const eventType = String(event?.event_type || "").trim().toLowerCase();
  const actorEmail = normalizeTimelineEmail(event?.actor_email);
  const interviewerEmail = normalizeTimelineEmail(booking?.interviewer_email);
  const candidateEmail = normalizeTimelineEmail(booking?.candidate_email);
  const hostEmail = normalizeTimelineEmail(booking?.manually_created_by);

  if (actorEmail && candidateEmail && actorEmail === candidateEmail) {
    return "candidate";
  }
  if (eventType === "created" || eventType === "rescheduled") {
    return "host";
  }
  if (eventType === "completed") {
    if (actorEmail && hostEmail && actorEmail === hostEmail && actorEmail !== interviewerEmail) {
      return "host";
    }
    return "interviewer";
  }
  if (actorEmail && interviewerEmail && actorEmail === interviewerEmail) {
    return "interviewer";
  }
  if (actorEmail && hostEmail && actorEmail === hostEmail) {
    return "host";
  }
  return "";
};

const resolveTimelineActorLine = (event, booking) => {
  const actorEmail = String(event?.actor_email || "").trim();
  const actorRole = resolveTimelineActorRole(event, booking);
  if (!actorEmail && !actorRole) return "";
  const roleLabel =
    actorRole === "candidate"
      ? "Candidate"
      : actorRole === "interviewer"
        ? "Interviewer"
        : actorRole === "host"
          ? "Host"
          : "";
  if (actorEmail && roleLabel) return `${roleLabel}: ${actorEmail}`;
  return actorEmail || roleLabel;
};

const TimelineDetailRow = ({ label, value }) => {
  if (value == null || value === "") return null;
  return (
    <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] items-start gap-x-3 gap-y-1">
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="min-w-0 whitespace-pre-line break-words text-sm font-medium text-slate-800">
        {value}
      </dd>
    </div>
  );
};

const SLOT_LABEL_PATTERNS = [
  /Scheduled slot:\s*([^.]+)/i,
  /Completed slot:\s*([^.]+)/i,
  /Cancelled slot:\s*([^.]+)/i,
  /Declined slot:\s*([^.]+)/i,
  /Current slot:\s*([^.]+)/i,
  /New slot:\s*([^.]+)/i,
  /Old slot:\s*([^.]+)/i,
];

const extractSlotLabelFromDescription = (description) => {
  const text = String(description || "").trim();
  if (!text) return "";
  for (const pattern of SLOT_LABEL_PATTERNS) {
    const match = pattern.exec(text);
    if (match?.[1]) {
      return match[1].trim();
    }
  }
  return "";
};

const sortTimelineEvents = (events = []) =>
  [...events].sort((left, right) => {
    const leftTime = parseInterviewRoomDate(left?.created_at)?.getTime() || 0;
    const rightTime = parseInterviewRoomDate(right?.created_at)?.getTime() || 0;
    return leftTime - rightTime;
  });

const getInterviewHistoryBadgeClassName = (statusKey) => {
  const key = String(statusKey || "").trim().toLowerCase();
  switch (key) {
    case "completed":
      return "border-slate-200 bg-slate-100 text-slate-700";
    case "joined":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "not_joined":
      return "border-orange-200 bg-orange-100 text-orange-700";
    case "upcoming":
      return "border-sky-200 bg-sky-100 text-sky-800";
    case "pending":
      return "border-amber-200 bg-amber-100 text-amber-800";
    case "decline":
      return "border-rose-200 bg-rose-50 text-rose-700";
    case "rescheduled":
      return "border-violet-200 bg-violet-50 text-violet-700";
    case "confirmed":
    case "confirmed_by_candidate":
    case "confirmed_by_interviewer":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";

    // Back-compat: older fallback keys based on event types.
    case "cancelled":
    case "canceled":
    case "declined":
      return "border-rose-200 bg-rose-50 text-rose-700";
    case "link_expired":
    case "created":
      return "border-amber-200 bg-amber-100 text-amber-800";
    default:
      return getTimelineEventBadgeClassName(statusKey);
  }
};

const resolveInterviewHistoryStatus = (events = []) => {
  const priority = [
    "completed",
    "cancelled",
    "declined",
    "link_expired",
    "reschedule_requested",
    "rescheduled",
    "created",
  ];
  const sorted = sortTimelineEvents(events);
  for (const eventType of priority) {
    const match = [...sorted].reverse().find((event) => event.event_type === eventType);
    if (match) {
      return {
        key: eventType,
        label: getTimelineEventLabel(eventType),
      };
    }
  }
  const latest = sorted[sorted.length - 1];
  return {
    key: latest?.event_type || "update",
    label: getTimelineEventLabel(latest?.event_type),
  };
};

const buildInterviewHistoryGroups = (events = [], currentBookingId = "") => {
  const safeCurrentId = String(currentBookingId || "").trim();
  const grouped = new Map();

  events.forEach((event) => {
    const bookingId = String(event?.booking_id || "").trim();
    if (!bookingId) return;
    if (!grouped.has(bookingId)) {
      grouped.set(bookingId, []);
    }
    grouped.get(bookingId).push(event);
  });

  return Array.from(grouped.entries())
    .map(([bookingId, bookingEvents]) => {
      const sortedEvents = sortTimelineEvents(bookingEvents);
      const slotLabel =
        sortedEvents
          .map((event) => extractSlotLabelFromDescription(event.description))
          .find(Boolean) || "Interview slot unavailable";
      const backendKey =
        sortedEvents.find((e) => e?.booking_history_status_key)?.booking_history_status_key ||
        sortedEvents.find((e) => e?.booking_outcome_key)?.booking_outcome_key;
      const backendLabel =
        sortedEvents.find((e) => e?.booking_history_status_label)?.booking_history_status_label ||
        null;
      const status = backendKey
        ? { key: backendKey, label: backendLabel || String(backendKey) }
        : resolveInterviewHistoryStatus(sortedEvents);
      const latestTimestamp =
        sortedEvents[sortedEvents.length - 1]?.created_at ||
        sortedEvents[0]?.created_at ||
        null;

      return {
        bookingId,
        isCurrent: bookingId === safeCurrentId,
        events: sortedEvents,
        slotLabel,
        status,
        latestTimestamp,
      };
    })
    .sort((left, right) => {
      const leftTime = parseInterviewRoomDate(left.latestTimestamp)?.getTime() || 0;
      const rightTime = parseInterviewRoomDate(right.latestTimestamp)?.getTime() || 0;
      return rightTime - leftTime;
    });
};

const COMPLETED_ACTIVITY_RETENTION_MS = 2 * 24 * 60 * 60 * 1000;

const TERMINAL_TIMELINE_EVENT_TYPES = new Set([
  "completed",
  "cancelled",
  "declined",
  "link_expired",
]);

const isEmailTimelineEvent = (eventType) =>
  String(eventType || "").trim().toLowerCase().startsWith("email_");

const getInterviewGroupCompletedAt = (group) => {
  const completedEvent = sortTimelineEvents(group?.events || [])
    .filter(
      (event) => String(event?.event_type || "").trim().toLowerCase() === "completed"
    )
    .pop();
  if (completedEvent?.created_at) {
    return parseInterviewRoomDate(completedEvent.created_at);
  }
  return parseInterviewRoomDate(group?.latestTimestamp);
};

const isStaleCompletedInterviewGroup = (group) => {
  const statusKey = String(group?.status?.key || "").trim().toLowerCase();
  if (statusKey !== "completed") return false;
  const completedAt = getInterviewGroupCompletedAt(group);
  if (!completedAt) return false;
  return Date.now() - completedAt.getTime() > COMPLETED_ACTIVITY_RETENTION_MS;
};

const getBookingCompletedAt = (booking) => {
  if (!booking) return null;
  const candidates = [
    booking?.live_room_ended_at,
    booking?.updated_at,
    booking?.scheduled_end_time,
  ];
  for (const value of candidates) {
    const parsed = parseInterviewRoomDate(value);
    if (parsed) return parsed;
  }
  return null;
};

const isStaleCompletedBooking = (booking) => {
  const status = resolveInterviewRoomStatus(booking);
  if (status.key !== "completed") return false;
  const completedAt = getBookingCompletedAt(booking);
  if (!completedAt) return false;
  return Date.now() - completedAt.getTime() > COMPLETED_ACTIVITY_RETENTION_MS;
};

const buildHistoryChipList = (events = [], emailDelivery = null) => {
  const milestoneChips = [];
  let emailChip = null;
  let emailChipAdded = false;
  let createdChipAdded = false;
  let terminalChip = null;

  sortTimelineEvents(events).forEach((event, index) => {
    const eventType = String(event?.event_type || "").trim().toLowerCase();
    const chip = {
      key: event.event_id || `${eventType}-${event.created_at}-${index}`,
      type: isEmailTimelineEvent(eventType) ? "email" : "timeline",
      label: isEmailTimelineEvent(eventType)
        ? emailDelivery?.summaryLabel || getTimelineEventLabel(eventType)
        : getTimelineChipLabel(event),
      badgeClass: isEmailTimelineEvent(eventType)
        ? getInterviewRoomEmailStatusBadgeClassName(
            emailDelivery?.summaryStatus ||
              (eventType === "email_success" ? "sent" : "unknown")
          )
        : getTimelineEventBadgeClassName(eventType),
      event,
    };

    if (isEmailTimelineEvent(eventType)) {
      if (emailChipAdded) return;
      emailChipAdded = true;
      emailChip = chip;
      return;
    }

    if (TERMINAL_TIMELINE_EVENT_TYPES.has(eventType)) {
      terminalChip = chip;
      return;
    }

    if (eventType === "created") {
      if (createdChipAdded) return;
      createdChipAdded = true;
      milestoneChips.push(chip);
      return;
    }

    milestoneChips.push(chip);
  });

  if (emailDelivery && !emailChipAdded) {
    emailChip = {
      key: "email-delivery",
      type: "email",
      label: emailDelivery.summaryLabel,
      badgeClass: getInterviewRoomEmailStatusBadgeClassName(emailDelivery.summaryStatus),
      event: null,
    };
  }

  return [
    ...milestoneChips,
    ...(emailChip ? [emailChip] : []),
    ...(terminalChip ? [terminalChip] : []),
  ];
};

const HistoryEventChips = ({
  events,
  emailDelivery,
  onOpenEmailDetails,
  onOpenTimelineEvent,
}) => {
  const chips = buildHistoryChipList(events, emailDelivery);
  if (!chips.length) return null;

  return (
    <>
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          onClick={() => {
            if (chip.type === "email") {
              if (onOpenEmailDetails) {
                onOpenEmailDetails();
              } else if (chip.event) {
                onOpenTimelineEvent?.(chip.event);
              }
              return;
            }
            onOpenTimelineEvent?.(chip.event);
          }}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors hover:opacity-90",
            chip.badgeClass
          )}
        >
          {chip.type === "email" ? <Mail className="h-3 w-3" /> : null}
          {chip.label}
          <ChevronRight className="h-3 w-3 opacity-60" />
        </button>
      ))}
    </>
  );
};

const TimelineEventDetailDialog = ({ open, onOpenChange, event, timezone, booking }) => {
  if (!event) return null;
  const eventType = String(event.event_type || "").trim().toLowerCase();
  const reminderEvent = isReminderTimelineEvent(event);
  const reminderRecipients = reminderEvent
    ? resolveReminderRecipientEmails(event, booking)
    : [];
  const reminderSender = reminderEvent ? resolveReminderSenderEmail(event) : "";
  const reminderDate =
    formatTimelineTimestamp(event.created_at, timezone) || "Not recorded";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          {reminderEvent ? (
            <>
              <DialogTitle>Reminder details</DialogTitle>
              <DialogDescription className="sr-only">
                Reminder event, status, date, recipient, and sender when available.
              </DialogDescription>
            </>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <DialogTitle>{event.title || "Interview update"}</DialogTitle>
                <Badge
                  variant="outline"
                  className={getTimelineEventBadgeClassName(eventType)}
                >
                  {getTimelineEventLabel(eventType)}
                </Badge>
              </div>
              <DialogDescription>
                {formatTimelineTimestamp(event.created_at, timezone) || "Update details"}
              </DialogDescription>
            </>
          )}
        </DialogHeader>
        {reminderEvent ? (
          <dl className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/80 p-4">
            <TimelineDetailRow label="Event" value={getReminderEventLabel(event)} />
            <TimelineDetailRow label="Status" value={getReminderStatusLabel(event)} />
            <TimelineDetailRow label="Date" value={reminderDate} />
            <TimelineDetailRow
              label="Sent to"
              value={
                reminderRecipients.length
                  ? reminderRecipients.join("\n")
                  : "Recipient not recorded"
              }
            />
            {reminderSender ? (
              <TimelineDetailRow label="Sent by" value={reminderSender} />
            ) : null}
          </dl>
        ) : (
          <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/80 p-4">
            {event.description ? (
              <Text size="sm" className="text-slate-700">
                {event.description}
              </Text>
            ) : (
              <Text size="sm" className="text-slate-500">
                No additional details recorded for this update.
              </Text>
            )}
            {event.actor_email || event?.event_data?.actor ? (
              <Text size="xs" className="text-slate-500">
                {resolveTimelineActorLine(event, booking)}
              </Text>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const EmailDeliveryDialog = ({ open, onOpenChange, emailDelivery }) => {
  if (!emailDelivery) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{emailDelivery.title}</DialogTitle>
          <DialogDescription>{emailDelivery.helperText}</DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
          {emailDelivery.entries.map((entry) => (
            <li
              key={entry.label}
              className="flex items-center justify-between gap-3 px-4 py-3"
            >
              <div className="min-w-0">
                <Text size="sm" className="font-medium text-slate-800">
                  {entry.label}
                </Text>
                <Text size="xs" className="truncate text-slate-500">
                  {entry.detail}
                </Text>
              </div>
              <Badge
                variant="outline"
                className={getInterviewRoomEmailStatusBadgeClassName(entry.status)}
              >
                {entry.statusLabel}
              </Badge>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
};

const RecordingsDialog = ({
  open,
  onOpenChange,
  recordings,
  recordingBusyId,
  onDownload,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Room recordings</DialogTitle>
        <DialogDescription>
          Recordings captured during this Search Job Meet session.
        </DialogDescription>
      </DialogHeader>
      {recordings.length ? (
        <ul className="space-y-2">
          {recordings.map((recording) => {
            const itemState = getLiveInterviewRecordingListItemState(recording);
            const busy = recordingBusyId === recording.recording_id;
            return (
              <li
                key={recording.recording_id}
                className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3"
              >
                <div className="min-w-0">
                  <Text size="sm" className="font-medium text-slate-900">
                    Recording {recording.recording_id?.slice?.(0, 8) || ""}
                  </Text>
                  <Text
                    size="xs"
                    className={itemState.isFailed ? "text-red-600" : "text-slate-500"}
                  >
                    {recording.started_at
                      ? new Date(recording.started_at).toLocaleString()
                      : "Ready"}{" "}
                    · {itemState.statusLabel}
                  </Text>
                  {itemState.isFailed ? (
                    <Text size="xs" className="mt-1 text-red-600">
                      {itemState.failureReason ||
                        "This recording could not be saved."}
                    </Text>
                  ) : null}
                </div>
                {itemState.canDownload ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onDownload(recording.recording_id)}
                    disabled={busy}
                    aria-label="Download recording"
                  >
                    {busy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <Text size="sm" className="text-slate-500">
          No recordings available yet.
        </Text>
      )}
    </DialogContent>
  </Dialog>
);

const MeetingActivityCard = ({
  group,
  timezone,
  isCurrent = false,
  statusInfo,
  scheduleInfo,
  isInstantMeet,
  helperText,
  emailDelivery,
  onOpenEmailDetails,
  onOpenTimelineEvent,
  recordingsCount = 0,
  onOpenRecordings,
  actionsSlot,
  statusPanel,
  joinAlert,
  schedulingNote,
  availabilityWindows = [],
  feedback,
  roomError,
  roomLoading,
  cancelReason = "",
}) => {
  if (!group?.events?.length && !isCurrent) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          {isCurrent ? (
            <Text size="xs" className="font-semibold uppercase tracking-[0.12em] text-slate-500">
              Current interview
            </Text>
          ) : null}
          {isCurrent ? (
            <>
              <h3 className="text-lg font-semibold leading-snug text-slate-950">
                {scheduleInfo?.dateLabel ||
                  (isInstantMeet ? "Available now" : group?.slotLabel) ||
                  "—"}
              </h3>
              {!isInstantMeet &&
              (scheduleInfo?.timeRangeLabel || scheduleInfo?.timezoneLabel) ? (
                <Text size="xs" className="text-slate-600">
                  {[scheduleInfo.timeRangeLabel, scheduleInfo.timezoneLabel]
                    .filter(Boolean)
                    .join(" · ")}
                </Text>
              ) : null}
            </>
          ) : (
            <Text size="sm" className="font-semibold text-slate-900">
              {group.slotLabel}
            </Text>
          )}
          {helperText && isCurrent ? (
            <Text size="xs" className="text-slate-500">
              {helperText}
            </Text>
          ) : null}
        </div>
        {isCurrent && statusInfo ? (
          <Badge
            variant="outline"
            title={
              statusInfo.key === "cancelled" && cancelReason
                ? `Reason: ${cancelReason}`
                : undefined
            }
            className={getInterviewRoomStatusBadgeClassName(statusInfo.key)}
          >
            {statusInfo.label}
          </Badge>
        ) : group?.status ? (
          <Badge
            variant="outline"
            title={
              String(group.status.key || "").toLowerCase() === "cancelled" && cancelReason
                ? `Reason: ${cancelReason}`
                : undefined
            }
            className={getInterviewHistoryBadgeClassName(group.status.key)}
          >
            {group.status.label}
          </Badge>
        ) : statusInfo ? (
          <Badge
            variant="outline"
            title={
              statusInfo.key === "cancelled" && cancelReason
                ? `Reason: ${cancelReason}`
                : undefined
            }
            className={getInterviewRoomStatusBadgeClassName(statusInfo.key)}
          >
            {statusInfo.label}
          </Badge>
        ) : null}
      </div>

      {isCurrent && actionsSlot ? (
        <div className="mt-3 border-t border-slate-100 pt-3">{actionsSlot}</div>
      ) : null}

      {isCurrent && statusPanel ? (
        <div className="mt-3 border-t border-slate-100 pt-3">{statusPanel}</div>
      ) : null}

      {isCurrent && (joinAlert || schedulingNote || availabilityWindows.length) ? (
        <div className="mt-2 space-y-2">
          {joinAlert}
          {schedulingNote}
          {availabilityWindows.length ? (
            <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2">
              <Text size="xs" className="font-semibold uppercase tracking-wide text-violet-800">
                Shared availability
              </Text>
              <ul className="mt-1 space-y-1">
                {availabilityWindows.map((window) => (
                  <li key={window.id}>
                    <Text size="xs" className="text-violet-950">
                      {window.label}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      {roomError && isCurrent ? (
        <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {roomError}
        </div>
      ) : null}

      {group?.events?.length ||
      (isCurrent && (emailDelivery || recordingsCount > 0)) ? (
        <div
          className={cn(
            "mt-3 flex flex-wrap gap-2",
            isCurrent ? "border-t border-slate-100 pt-3" : "pt-1"
          )}
        >
          <HistoryEventChips
            events={group?.events || []}
            emailDelivery={isCurrent ? emailDelivery : null}
            onOpenEmailDetails={isCurrent ? onOpenEmailDetails : undefined}
            onOpenTimelineEvent={onOpenTimelineEvent}
          />
          {isCurrent && recordingsCount > 0 ? (
            <button
              type="button"
              onClick={onOpenRecordings}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100"
            >
              <Video className="h-3 w-3" />
              {recordingsCount} recording{recordingsCount === 1 ? "" : "s"}
              <ChevronRight className="h-3 w-3 opacity-60" />
            </button>
          ) : null}
          {isCurrent && roomLoading ? (
            <span className="inline-flex items-center gap-1 text-xs text-slate-400">
              <Loader2 className="h-3 w-3 animate-spin" />
              Syncing room
            </span>
          ) : null}
        </div>
      ) : null}

      {isCurrent && feedback ? (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <InterviewerFeedbackSummary feedback={feedback} compact />
        </div>
      ) : null}
    </div>
  );
};

const InterviewRoomDialog = ({
  open,
  onClose,
  embedded = false,
  booking,
  candidate,
  jobDetails,
  viewerRole = "interviewer",
  onBookingUpdated,
}) => {
  const isPanelActive = embedded || open;
  const onBookingUpdatedRef = useRef(onBookingUpdated);
  const bookingRefreshInFlightRef = useRef(false);
  const rescheduleFormInitializedRef = useRef(false);
  useEffect(() => {
    onBookingUpdatedRef.current = onBookingUpdated;
  }, [onBookingUpdated]);
  const [transcriptPdfBusy, setTranscriptPdfBusy] = useState(false);
  const [roomLoading, setRoomLoading] = useState(false);
  const [roomError, setRoomError] = useState("");
  const [roomPayload, setRoomPayload] = useState(null);
  const [recordingBusyId, setRecordingBusyId] = useState("");
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleBusy, setRescheduleBusy] = useState(false);
  const [reminderBusyParticipant, setReminderBusyParticipant] = useState("");
  const [proceedBusy, setProceedBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelReasonOpen, setCancelReasonOpen] = useState(false);
  const [cancelReasonDraft, setCancelReasonDraft] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");
  const [rescheduleError, setRescheduleError] = useState("");
  const [rescheduleTimezone, setRescheduleTimezone] = useState(DEFAULT_MEETING_TIMEZONE);
  const [rescheduleDraft, setRescheduleDraft] = useState(() => createRescheduleSlotDraft());
  const [selectedSuggestionId, setSelectedSuggestionId] = useState("");
  const [emailDeliveryOpen, setEmailDeliveryOpen] = useState(false);
  const [recordingsOpen, setRecordingsOpen] = useState(false);
  const [timelineEventOpen, setTimelineEventOpen] = useState(false);
  const [selectedTimelineEvent, setSelectedTimelineEvent] = useState(null);

  const effectiveBooking = useMemo(
    () => mergeLiveMeetBookingWithRoom(booking, roomPayload?.room),
    [booking, roomPayload?.room]
  );

  const candidateName =
    candidate?.name ||
    candidate?.full_name ||
    effectiveBooking?.candidate_name ||
    effectiveBooking?.candidate_email ||
    "Candidate";
  const statusInfo = useMemo(
    () => resolveInterviewRoomStatus(effectiveBooking),
    [effectiveBooking]
  );
  const scheduleInfo = useMemo(
    () => formatInterviewRoomSchedule(effectiveBooking),
    [effectiveBooking]
  );
  const rescheduleSuggestedSlots = useMemo(
    () => getRescheduleSuggestedSlots(effectiveBooking),
    [effectiveBooking]
  );
  const isReschedulePending = isInterviewRoomReschedulePending(effectiveBooking);
  const isInstantMeet = isInstantLiveMeetBooking(effectiveBooking);
  const isCandidateView = viewerRole === "candidate";
  const isLiveMeetManualDualFlow =
    !isCandidateView &&
    effectiveBooking?.scheduling_mode === "manual" &&
    isLiveMeetBookingProvider(effectiveBooking?.meeting_provider);
  const confirmationStatuses = effectiveBooking?.confirmation_statuses || null;
  const pendingConfirmationParticipants = Array.isArray(
    confirmationStatuses?.pending_participants
  )
    ? confirmationStatuses.pending_participants
    : [];
  const joinUrl = useMemo(
    () => getInterviewRoomJoinUrl(effectiveBooking, viewerRole),
    [effectiveBooking, viewerRole]
  );
  const shareUrl = useMemo(
    () => getInterviewRoomShareUrl(effectiveBooking, viewerRole),
    [effectiveBooking, viewerRole]
  );
  const canJoin = Boolean(effectiveBooking?.can_join && joinUrl);
  const joinReason = String(effectiveBooking?.join_reason || "")
    .trim()
    .toLowerCase();
  const joinDisabledLabel =
    joinReason === "rescheduled" || statusInfo.key === "reschedule_pending"
      ? "Reschedule pending"
      : joinReason === "not_open_yet" && joinUrl
        ? "Join opens soon"
        : joinReason === "expired" || statusInfo.key === "not_joined"
          ? "Link expired"
          : "Join unavailable";
  const recordings = roomPayload?.recordings || [];
  const roomId = effectiveBooking?.live_room_id;
  const canViewTranscript =
    !isCandidateView && canViewInterviewRoomTranscript(effectiveBooking);
  const todayDateValue = useMemo(() => getTodayDateString(), []);
  const resolvedTimezone = useMemo(() => {
    const candidateTimezone = String(scheduleInfo.timezoneLabel || "").trim();
    return isValidIanaTimezone(candidateTimezone)
      ? candidateTimezone
      : DEFAULT_MEETING_TIMEZONE;
  }, [scheduleInfo.timezoneLabel]);
  const rescheduleCurrentSlot = useMemo(
    () => {
      const scheduledStartTime = isReschedulePending
        ? effectiveBooking?.original_scheduled_start_time ||
          effectiveBooking?.scheduled_start_time
        : effectiveBooking?.scheduled_start_time;
      const scheduledEndTime = isReschedulePending
        ? effectiveBooking?.original_scheduled_end_time ||
          effectiveBooking?.scheduled_end_time
        : effectiveBooking?.scheduled_end_time;
      return buildRescheduleSlotsFromBooking({
        scheduledStartTime,
        scheduledEndTime,
        timezone: resolvedTimezone,
        formatDate: formatInputDateInTimezone,
        formatTime: formatInputTimeInTimezone,
      }).draft;
    },
    [
      effectiveBooking?.original_scheduled_end_time,
      effectiveBooking?.original_scheduled_start_time,
      effectiveBooking?.scheduled_end_time,
      effectiveBooking?.scheduled_start_time,
      isReschedulePending,
      resolvedTimezone,
    ]
  );
  const jobTitle =
    jobDetails?.job_title ||
    jobDetails?.title ||
    candidate?.job_title ||
    "Live Interview";

  const loadRoomDetails = useCallback(async () => {
    if (!roomId || isCandidateView) {
      setRoomPayload(null);
      setRoomError("");
      return;
    }
    setRoomLoading(true);
    setRoomError("");
    try {
      const payload = await getLiveInterviewRoom(roomId, { role: viewerRole });
      setRoomPayload(payload || null);
    } catch (err) {
      setRoomPayload(null);
      setRoomError(err?.backendMessage || err?.message || "Unable to load room details.");
    } finally {
      setRoomLoading(false);
    }
  }, [isCandidateView, roomId, viewerRole]);

  useEffect(() => {
    if (!isPanelActive) return;
    loadRoomDetails();
  }, [loadRoomDetails, isPanelActive]);

  useEffect(() => {
    if (!onBookingUpdatedRef.current || !isPanelActive || !roomPayload?.room) {
      return;
    }
    const merged = mergeLiveMeetBookingWithRoom(booking, roomPayload.room);
    const roomEnded = isLiveMeetRoomClosed(merged);
    if (
      merged?.can_join === booking?.can_join &&
      merged?.meeting_completed === booking?.meeting_completed &&
      !roomEnded
    ) {
      return;
    }
    if (bookingRefreshInFlightRef.current) {
      return;
    }
    bookingRefreshInFlightRef.current = true;
    Promise.resolve(
      onBookingUpdatedRef.current(undefined, { includeTimeline: true })
    ).finally(() => {
      bookingRefreshInFlightRef.current = false;
    });
  }, [booking, isPanelActive, roomPayload?.room]);

  useEffect(() => {
    if (isPanelActive) return;
    setRescheduleOpen(false);
    setRescheduleBusy(false);
    setRescheduleError("");
  }, [isPanelActive]);

  const initializeRescheduleForm = useCallback(() => {
    const timezone = resolvedTimezone;
    setRescheduleTimezone(timezone);
    const scheduledStartTime = isReschedulePending
      ? effectiveBooking?.original_scheduled_start_time ||
        effectiveBooking?.scheduled_start_time
      : effectiveBooking?.scheduled_start_time;
    const scheduledEndTime = isReschedulePending
      ? effectiveBooking?.original_scheduled_end_time ||
        effectiveBooking?.scheduled_end_time
      : effectiveBooking?.scheduled_end_time;
    const nextState = buildRescheduleSlotsFromBooking({
      scheduledStartTime,
      scheduledEndTime,
      timezone,
      formatDate: formatInputDateInTimezone,
      formatTime: formatInputTimeInTimezone,
    });
    const suggestions = getRescheduleSuggestedSlots(effectiveBooking);
    const firstSuggestion = suggestions[0];

    if (isReschedulePending && firstSuggestion) {
      setRescheduleDraft(
        createRescheduleSlotDraft({
          date: firstSuggestion.date || nextState.draft.date,
          startTime: "",
          endTime: "",
        })
      );
      setSelectedSuggestionId(firstSuggestion.id || "");
    } else {
      setRescheduleDraft(nextState.draft);
      setSelectedSuggestionId("");
    }
    setRescheduleError("");
  }, [
    effectiveBooking,
    effectiveBooking?.original_scheduled_end_time,
    effectiveBooking?.original_scheduled_start_time,
    effectiveBooking?.scheduled_end_time,
    effectiveBooking?.scheduled_start_time,
    isReschedulePending,
    resolvedTimezone,
  ]);

  const applySuggestedRescheduleSlot = useCallback(
    (suggestion) => {
      if (!suggestion) return;
      const nextTimezone = String(
        suggestion.timezone || rescheduleTimezone || resolvedTimezone
      ).trim();
      if (nextTimezone && isValidIanaTimezone(nextTimezone)) {
        setRescheduleTimezone(nextTimezone);
      }
      if (isReschedulePending) {
        setRescheduleDraft(
          createRescheduleSlotDraft({
            date: suggestion.date || "",
            startTime: "",
            endTime: "",
          })
        );
        setSelectedSuggestionId(suggestion.id || "");
        setRescheduleError("");
        return;
      }
      const nextDraft = suggestionToRescheduleDraft(
        suggestion,
        nextTimezone || rescheduleTimezone
      );
      if (nextDraft) {
        setRescheduleDraft(nextDraft);
        setSelectedSuggestionId(suggestion.id || "");
        setRescheduleError("");
      }
    },
    [isReschedulePending, rescheduleTimezone, resolvedTimezone]
  );

  useEffect(() => {
    if (!rescheduleOpen) {
      rescheduleFormInitializedRef.current = false;
      return;
    }
    if (rescheduleFormInitializedRef.current) {
      return;
    }
    rescheduleFormInitializedRef.current = true;
    initializeRescheduleForm();
  }, [initializeRescheduleForm, rescheduleOpen]);

  const handleJoin = () => {
    if (!canJoin) return;
    if (!openLiveMeetRoomFromBooking(effectiveBooking) && joinUrl) {
      window.open(joinUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleCopyLink = async () => {
    try {
      await copyText(shareUrl);
      toast.success("Co-host invite link copied. Share it with the interview panel.");
    } catch (err) {
      toast.error(err?.message || "Unable to copy the interviewer link.");
    }
  };

  const handleViewTranscriptPdf = async () => {
    if (!roomId) {
      toast.error("No Search Job Meet room is linked to this meeting.");
      return;
    }
    setTranscriptPdfBusy(true);
    try {
      const payload = await fetchLiveMeetTranscriptPdfUrl({ liveRoomId: roomId });
      const pdfUrl = String(payload?.pdf_url || payload?.transcript_pdf?.url || "").trim();
      if (!pdfUrl) {
        throw new Error(payload?.message || "Transcript PDF is not available yet.");
      }
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to open transcript PDF."
      );
    } finally {
      setTranscriptPdfBusy(false);
    }
  };

  const handleRecordingDownload = async (recordingId) => {
    if (!roomId || !recordingId) return;
    const selected = recordings.find(
      (item) => String(item?.recording_id || "") === String(recordingId)
    );
    const itemState = getLiveInterviewRecordingListItemState(selected);
    if (!itemState.canDownload) {
      toast.error(
        itemState.failureReason || "Recording download is not available."
      );
      return;
    }
    setRecordingBusyId(recordingId);
    try {
      const payload = await getLiveInterviewRecordingDownloadUrl(roomId, recordingId, {
        role: viewerRole,
      });
      const downloadUrl =
        payload?.download?.url ||
        payload?.download?.download_url ||
        payload?.download_url ||
        payload?.url ||
        "";
      if (!downloadUrl) {
        throw new Error("Recording download is not available yet.");
      }
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err?.backendMessage || err?.message || "Unable to open the recording.");
    } finally {
      setRecordingBusyId("");
    }
  };

  const helperText = getInterviewRoomStatusMessage(effectiveBooking);
  const emailDelivery = getInterviewRoomEmailDelivery(effectiveBooking);
  const timelineEvents = Array.isArray(effectiveBooking?.timeline)
    ? effectiveBooking.timeline.filter((item) => item && item.created_at)
    : [];
  const interviewHistoryGroups = useMemo(() => {
    const groups = buildInterviewHistoryGroups(
      timelineEvents,
      effectiveBooking?.booking_id
    );
    return groups.filter((group) => !isStaleCompletedInterviewGroup(group));
  }, [timelineEvents, effectiveBooking?.booking_id]);
  const isCurrentBookingStaleCompleted = useMemo(
    () => isStaleCompletedBooking(effectiveBooking),
    [effectiveBooking]
  );
  const currentInterviewHistory = interviewHistoryGroups.find((group) => group.isCurrent) || null;
  const previousInterviewHistory = interviewHistoryGroups.filter((group) => !group.isCurrent);
  const showActivityBooking = Boolean(effectiveBooking) && !isCurrentBookingStaleCompleted;
  const showShareLink =
    canJoin &&
    Boolean(shareUrl) &&
    !["completed", "cancelled", "declined", "expired", "reschedule_pending"].includes(statusInfo.key);
  const normalizedBookingStatus = String(effectiveBooking?.status || "")
    .trim()
    .toLowerCase();
  const canRescheduleBookingAction =
    !isInstantMeet &&
    !isCandidateView &&
    Boolean(effectiveBooking?.booking_id) &&
    statusInfo.key !== "cancelled" &&
    statusInfo.key !== "completed" &&
    statusInfo.key !== "expired" &&
    !effectiveBooking?.meeting_completed &&
    ![
      "cancelled",
      "canceled",
      "completed",
      "ended",
      "closed",
    ].includes(normalizedBookingStatus);
  // A no-show whose join window has expired is represented as `not_joined`.
  // It can still be rescheduled, but offering a cancellation at that point is
  // misleading because the interview is already unavailable to join.
  const isExpiredUnjoinedInterview =
    joinReason === "expired" || ["expired", "not_joined"].includes(statusInfo.key);
  const canCancelBookingAction =
    canRescheduleBookingAction && !isExpiredUnjoinedInterview;
  const isCancelledInterview =
    statusInfo.key === "cancelled" ||
    ["cancelled", "canceled"].includes(normalizedBookingStatus);
  const isDeclinedInterview =
    statusInfo.key === "declined" ||
    ["declined", "declined_by_candidate", "declined_by_interviewer"].includes(
      normalizedBookingStatus
    );
  const isCompletedInterview =
    statusInfo.key === "completed" ||
    Boolean(effectiveBooking?.meeting_completed) ||
    isLiveMeetRoomClosed(effectiveBooking) ||
    ["completed", "ended", "closed"].includes(normalizedBookingStatus);
  const isTerminalInterview = isCancelledInterview || isDeclinedInterview;
  const visibleRoomError = isTerminalInterview ? "" : roomError;
  const showConfirmationStatusPanel =
    !isTerminalInterview &&
    !isCompletedInterview &&
    statusInfo.key !== "not_joined" &&
    isLiveMeetManualDualFlow &&
    confirmationStatuses;
  const canProceedWithoutBothConfirmations =
    showConfirmationStatusPanel &&
    pendingConfirmationParticipants.length > 0 &&
    normalizedBookingStatus !== "confirmed";
  const canSendConfirmationReminders =
    showConfirmationStatusPanel &&
    canSendLiveMeetConfirmationReminders(effectiveBooking);
  const pendingRescheduleActorLabel =
    normalizedBookingStatus === "reschedule_requested_by_interviewer"
      ? "The interviewer shared an availability window."
      : "The candidate shared an availability window.";
  const pendingRescheduleActorName =
    normalizedBookingStatus === "reschedule_requested_by_interviewer"
      ? "interviewer"
      : "candidate";
  const showRoomActivity =
    !isCandidateView &&
    (roomLoading ||
      Boolean(visibleRoomError) ||
      recordings.length > 0 ||
      statusInfo.key === "active" ||
      statusInfo.key === "completed");
  const showSchedulingNote =
    !isInstantMeet &&
    (canRescheduleBookingAction ||
      statusInfo.key === "reschedule_pending" ||
      ["scheduled", "starting_soon", "cancelled", "expired"].includes(statusInfo.key));
  const joinButtonLabel = isCandidateView ? "Join Interview" : "Join meeting";
  const transcriptButtonLabel = transcriptPdfBusy
    ? "Opening..."
    : canViewTranscript
      ? "View Transcript"
      : "Transcript Pending";
  const openTimelineEventDetails = (event) => {
    if (!event) return;
    setSelectedTimelineEvent(event);
    setTimelineEventOpen(true);
  };

  const handleRescheduleSubmit = async () => {
    if (!effectiveBooking?.booking_id) return;
    if (
      statusInfo.key === "completed" ||
      effectiveBooking?.meeting_completed ||
      normalizedBookingStatus === "completed"
    ) {
      setRescheduleError("Completed interviews cannot be rescheduled.");
      return;
    }

    setRescheduleError("");
    if (!rescheduleDraft?.date || !rescheduleDraft?.startTime || !rescheduleDraft?.endTime) {
      setRescheduleError("Please pick a date, start time, and end time.");
      return;
    }
    if (!isValidIanaTimezone(rescheduleTimezone)) {
      setRescheduleError("Select a valid timezone.");
      return;
    }

    const endTime = normalizeTimeValue(rescheduleDraft.endTime);
    const startIso = toIsoInTimezone(
      rescheduleDraft.date,
      rescheduleDraft.startTime,
      rescheduleTimezone
    );
    const endIso = toIsoInTimezone(
      rescheduleDraft.date,
      endTime,
      rescheduleTimezone
    );
    if (!startIso || !endIso) {
      setRescheduleError("Invalid date or time for the selected timezone.");
      return;
    }

    const windowError = validateRescheduleTimeWindow({
      startIso,
      endIso,
    });
    if (windowError) {
      setRescheduleError(windowError);
      return;
    }

    if (isReschedulePending && rescheduleSuggestedSlots.length) {
      if (!selectedSuggestionId) {
        setRescheduleError("Select an availability window before picking the final interview time.");
        return;
      }
      const availabilityError = validateRescheduleWithinAvailability({
        startIso,
        endIso,
        availabilityWindows: rescheduleSuggestedSlots,
        timezone: rescheduleTimezone,
      });
      if (availabilityError) {
        setRescheduleError(availabilityError);
        return;
      }
    }

    setRescheduleBusy(true);
    try {
      const response = await rescheduleLiveMeetBooking({
        booking_id: effectiveBooking.booking_id,
        start_time: startIso,
        end_time: endIso,
        timezone: rescheduleTimezone,
        job_title: jobTitle,
        meeting_title: jobTitle,
        interviewer_name: effectiveBooking?.interviewer_name || "",
        manual_note: "Rescheduled manually from Interview Room",
        client_request_id: createClientRequestId(),
      });
      const nextBooking = response?.booking || null;
      const emailStatus = nextBooking?.email_status;
      if (emailStatus === "failed") {
        const errMsg =
          nextBooking?.email_errors?.candidate ||
          nextBooking?.email_errors?.interviewer ||
          "Interview was rescheduled, but the updated emails could not be sent.";
        toast.warning(errMsg, { duration: 8000 });
      } else if (emailStatus === "partial") {
        toast.warning(
          "Interview rescheduled. One updated email may have failed.",
          { duration: 8000 }
        );
      } else if (emailStatus === "sending") {
        toast.success(
          response?.message ||
            "Interview rescheduled. Updated emails are being sent."
        );
      } else {
        toast.success(response?.message || "Interview rescheduled successfully.");
      }
      const warningMessages = Array.isArray(response?.warnings)
        ? response.warnings.filter(
            (item) => typeof item === "string" && item.trim()
          )
        : [];
      if (warningMessages.length > 0) {
        toast.warning(warningMessages.join(" "), { duration: 8000 });
      }
      setRescheduleOpen(false);
      await Promise.resolve(
        onBookingUpdated?.(nextBooking || undefined)
      );
      onClose?.();
    } catch (err) {
      const message =
        err?.message || "Unable to reschedule this interview right now.";
      setRescheduleError(message);
      toast.error(message);
    } finally {
      setRescheduleBusy(false);
    }
  };

  const handleSendConfirmationReminder = async (participant) => {
    if (!effectiveBooking?.booking_id || !participant) return;
    if (!canSendLiveMeetConfirmationReminders(effectiveBooking)) {
      toast.error(
        "This interview is already confirmed. Confirmation reminders are no longer sent."
      );
      return;
    }
    setReminderBusyParticipant(participant);
    try {
      const response = await sendLiveMeetConfirmationReminder({
        bookingId: effectiveBooking.booking_id,
        participant,
      });
      toast.success(
        response?.message ||
          `Reminder sent to the pending ${participant}.`
      );
      await Promise.resolve(onBookingUpdated?.(response?.booking || undefined));
    } catch (err) {
      toast.error(
        err?.message || "Unable to send the confirmation reminder right now."
      );
    } finally {
      setReminderBusyParticipant("");
    }
  };

  const handleProceedWithoutConfirmations = async () => {
    if (!effectiveBooking?.booking_id) return;
    const confirmed = window.confirm(
      "Proceed with this interview even though not everyone has confirmed yet?"
    );
    if (!confirmed) return;

    setProceedBusy(true);
    try {
      const response = await proceedLiveMeetWithoutConfirmations({
        bookingId: effectiveBooking.booking_id,
      });
      toast.success(
        response?.message ||
          "Interview marked as confirmed. Final meeting details have been sent."
      );
      const warningMessages = Array.isArray(response?.warnings)
        ? response.warnings.filter(
            (item) => typeof item === "string" && item.trim()
          )
        : [];
      if (warningMessages.length > 0) {
        toast.warning(warningMessages.join(" "), { duration: 8000 });
      }
      await Promise.resolve(onBookingUpdated?.(response?.booking || undefined));
    } catch (err) {
      toast.error(
        err?.message || "Unable to proceed without both confirmations."
      );
    } finally {
      setProceedBusy(false);
    }
  };

  const handleCancelBooking = () => {
    if (!effectiveBooking?.booking_id) return;
    setCancelReasonDraft("");
    setCancelReasonError("");
    setCancelReasonOpen(true);
  };

  const submitCancelBookingWithReason = async () => {
    if (!effectiveBooking?.booking_id || cancelBusy) return;
    const reason = String(cancelReasonDraft || "").trim();
    if (!reason) {
      setCancelReasonError("Please enter a reason for cancelling this interview.");
      return;
    }

    setCancelBusy(true);
    setCancelReasonError("");
    try {
      const response = await cancelLiveMeetBooking({
        bookingId: effectiveBooking.booking_id,
        reason,
      });
      toast.success(response?.message || "Interview cancelled successfully.");
      const warningMessages = Array.isArray(response?.warnings)
        ? response.warnings.filter(
            (item) => typeof item === "string" && item.trim()
          )
        : [];
      if (warningMessages.length > 0) {
        toast.warning(warningMessages.join(" "), { duration: 8000 });
      }
      setCancelReasonOpen(false);
      setCancelReasonDraft("");
      await Promise.resolve(onBookingUpdated?.(response?.booking || undefined));
    } catch (err) {
      toast.error(err?.message || "Unable to cancel this interview right now.");
    } finally {
      setCancelBusy(false);
    }
  };

  if (!isPanelActive) {
    return null;
  }

  const candidateConfirmation = confirmationStatuses?.candidate || null;
  const interviewerConfirmation = confirmationStatuses?.interviewer || null;
  const confirmationStatusPanel = showConfirmationStatusPanel ? (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <StatCard
          label="Candidate confirmation"
          value={candidateConfirmation?.label || "Pending"}
          detail={formatConfirmationStatusDetail(
            candidateConfirmation,
            scheduleInfo.timezoneLabel || resolvedTimezone,
            effectiveBooking?.host_confirmation_override
              ? "Host proceeded without the candidate confirmation."
              : "Waiting for the candidate response."
          )}
          icon={Users}
        />
        <StatCard
          label="Interviewer confirmation"
          value={interviewerConfirmation?.label || "Pending"}
          detail={formatConfirmationStatusDetail(
            interviewerConfirmation,
            scheduleInfo.timezoneLabel || resolvedTimezone,
            effectiveBooking?.host_confirmation_override
              ? "Host proceeded without the interviewer confirmation."
              : "Waiting for the interviewer response."
          )}
          icon={CheckCircle2}
        />
      </div>
      {effectiveBooking?.host_confirmation_override ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <Text size="xs" className="text-amber-900">
            This interview was marked confirmed by the host before both participants replied.
          </Text>
        </div>
      ) : null}
      {canSendConfirmationReminders || canProceedWithoutBothConfirmations ? (
        <div className="flex flex-wrap items-center gap-2">
          {canSendConfirmationReminders
            ? pendingConfirmationParticipants.map((participant) => (
            <Button
              key={participant}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleSendConfirmationReminder(participant)}
              disabled={Boolean(reminderBusyParticipant) || proceedBusy || cancelBusy}
            >
              {reminderBusyParticipant === participant ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Mail className="mr-1.5 h-3.5 w-3.5" />
              )}
              {participant === "candidate" ? "Remind candidate" : "Remind interviewer"}
            </Button>
              ))
            : null}
          {canProceedWithoutBothConfirmations ? (
            <Button
              type="button"
              size="sm"
              className="bg-amber-600 text-white hover:bg-amber-700"
              onClick={handleProceedWithoutConfirmations}
              disabled={proceedBusy || Boolean(reminderBusyParticipant) || cancelBusy}
            >
              {proceedBusy ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
              )}
              Proceed without both confirmations
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  ) : null;

  const mainPanel = (
    <>
          <div className={embedded ? "mb-3 space-y-2" : "space-y-3"}>
            {!embedded ? (
              <div className="flex flex-wrap items-center gap-2">
                <Badge
                  variant="outline"
                  title={
                    statusInfo.key === "cancelled" && effectiveBooking?.cancel_reason
                      ? `Reason: ${effectiveBooking.cancel_reason}`
                      : undefined
                  }
                  className={getInterviewRoomStatusBadgeClassName(statusInfo.key)}
                >
                  {statusInfo.label}
                </Badge>
                <Badge variant="outline" className="border-slate-200 bg-white text-slate-700">
                  {getInterviewRoomTypeLabel(effectiveBooking)}
                </Badge>
              </div>
            ) : null}
            {!embedded ? (
              <>
                <DialogTitle className="text-2xl font-semibold text-slate-950">
                  Activity
                </DialogTitle>
                <DialogDescription className="text-sm text-slate-600">
                  Manage the live interview room, transcript, and booking updates here.
                  Use Interview Scheduling to create a new interview.
                </DialogDescription>
              </>
            ) : null}
          </div>

          {!showActivityBooking ? (
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-12 text-center">
              <div
                className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"
                aria-hidden
              >
                <CalendarClock className="h-7 w-7" />
              </div>
              <div className="space-y-1">
                <Text size="sm" className="font-semibold text-slate-900">
                  {isCurrentBookingStaleCompleted
                    ? "No recent interview activity"
                    : "No interview scheduled yet"}
                </Text>
                <Text size="xs" className="text-slate-500">
                  {isCurrentBookingStaleCompleted ? (
                    <>
                      Completed interviews are removed from Activity after 2 days. Open{' '}
                      <strong className="font-semibold text-slate-700">Interview Scheduling</strong> to
                      schedule a new Search Job Meet for {candidateName}.
                    </>
                  ) : (
                    <>
                      Open the <strong className="font-semibold text-slate-700">Interview Scheduling</strong> tab to create a Search Job Meet for{' '}
                      {candidateName}. Their join link and the interviewer's host link will be emailed automatically.
                    </>
                  )}
                </Text>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <MeetingActivityCard
                group={
                  currentInterviewHistory || {
                    bookingId: effectiveBooking?.booking_id,
                    events: timelineEvents.filter(
                      (event) =>
                        String(event?.booking_id || "").trim() ===
                        String(effectiveBooking?.booking_id || "").trim()
                    ),
                    slotLabel: effectiveBooking?.slot_label || scheduleInfo.detailLabel,
                    status: {
                      key: statusInfo.key,
                      label: statusInfo.label,
                    },
                  }
                }
                timezone={scheduleInfo.timezoneLabel || resolvedTimezone}
                isCurrent
                statusInfo={statusInfo}
                scheduleInfo={scheduleInfo}
                isInstantMeet={isInstantMeet}
                helperText={helperText}
                emailDelivery={emailDelivery}
                onOpenEmailDetails={() => setEmailDeliveryOpen(true)}
                onOpenTimelineEvent={openTimelineEventDetails}
                recordingsCount={recordings.length}
                onOpenRecordings={() => setRecordingsOpen(true)}
                feedback={effectiveBooking?.interviewer_feedback}
                cancelReason={effectiveBooking?.cancel_reason || ""}
                roomError={visibleRoomError}
                roomLoading={roomLoading && showRoomActivity}
                joinAlert={
                  !canJoin && joinUrl ? (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                      <Clock3 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700" />
                      <Text size="xs" className="font-medium text-amber-800">
                        {getJoinUnavailableAlertMessage(effectiveBooking)}
                      </Text>
                    </div>
                  ) : null
                }
                schedulingNote={
                  isReschedulePending && canRescheduleBookingAction ? (
                    <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2">
                      <Text size="xs" className="text-sky-900">
                        {pendingRescheduleActorLabel} Click{" "}
                        <strong className="font-semibold">Pick New Time</strong> to choose the
                        final interview slot inside that availability window.
                      </Text>
                    </div>
                  ) : showSchedulingNote && !canRescheduleBookingAction ? (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <Text size="xs" className="text-slate-600">
                        This booking is no longer editable here. Open Interview Scheduling to create a new interview.
                      </Text>
                    </div>
                  ) : null
                }
                availabilityWindows={
                  isReschedulePending ? rescheduleSuggestedSlots : []
                }
                actionsSlot={
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      {!isDeclinedInterview ? (
                        <Button
                          type="button"
                          onClick={handleJoin}
                          disabled={!canJoin}
                          size="sm"
                          className="bg-emerald-700 text-white hover:bg-emerald-800 disabled:bg-slate-200 disabled:text-slate-500"
                        >
                          <Video className="mr-1.5 h-3.5 w-3.5" />
                          {canJoin ? joinButtonLabel : joinDisabledLabel}
                        </Button>
                      ) : null}
                      {showShareLink ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleCopyLink}
                        >
                          <Copy className="mr-1.5 h-3.5 w-3.5" />
                          Copy link
                        </Button>
                      ) : null}
                      {!isCandidateView ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleViewTranscriptPdf}
                          disabled={!canViewTranscript || transcriptPdfBusy}
                        >
                          <FileText className="mr-1.5 h-3.5 w-3.5" />
                          {transcriptButtonLabel}
                        </Button>
                      ) : null}
                      {canRescheduleBookingAction ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            rescheduleFormInitializedRef.current = false;
                            setRescheduleOpen(true);
                          }}
                          disabled={
                            rescheduleBusy ||
                            cancelBusy ||
                            proceedBusy ||
                            Boolean(reminderBusyParticipant)
                          }
                        >
                          <CalendarClock className="mr-1.5 h-3.5 w-3.5" />
                          {isReschedulePending ? "Pick New Time" : "Reschedule"}
                        </Button>
                      ) : null}
                      {canCancelBookingAction ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleCancelBooking}
                          disabled={
                            cancelBusy ||
                            rescheduleBusy ||
                            proceedBusy ||
                            Boolean(reminderBusyParticipant)
                          }
                        >
                          {cancelBusy ? (
                            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Clock3 className="mr-1.5 h-3.5 w-3.5" />
                          )}
                          Cancel interview
                        </Button>
                      ) : null}
                    </div>
                  </div>
                }
                statusPanel={confirmationStatusPanel}
              />

              {previousInterviewHistory.length ? (
                <>
                  <Text size="xs" className="px-1 font-semibold uppercase tracking-[0.1em] text-slate-400">
                    Previous interviews
                  </Text>
                  {previousInterviewHistory.map((group) => {
                    const previousCancelReason = [...(group.events || [])]
                      .reverse()
                      .map((event) => {
                        if (String(event?.event_type || "").toLowerCase() !== "cancelled") {
                          return "";
                        }
                        const fromData = String(event?.event_data?.reason || "").trim();
                        if (fromData) return fromData;
                        const description = String(event?.description || "");
                        const marker = description.toLowerCase().lastIndexOf("reason:");
                        if (marker < 0) return "";
                        return description.slice(marker + 7).trim().replace(/\.$/, "");
                      })
                      .find(Boolean) || "";
                    return (
                    <MeetingActivityCard
                      key={group.bookingId}
                      group={group}
                      timezone={scheduleInfo.timezoneLabel || resolvedTimezone}
                      onOpenTimelineEvent={openTimelineEventDetails}
                      cancelReason={previousCancelReason}
                    />
                    );
                  })}
                </>
              ) : null}
            </div>
          )}

          {!embedded ? (
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          ) : null}
    </>
  );

  const overlayDialogs = (
    <>
      <Dialog
        open={cancelReasonOpen}
        onOpenChange={(open) => {
          if (cancelBusy) return;
          setCancelReasonOpen(open);
          if (!open) {
            setCancelReasonDraft("");
            setCancelReasonError("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {effectiveBooking?.interview_round_label
                ? `Do you want to cancel the scheduled ${effectiveBooking.interview_round_label} interview?`
                : "Do you want to cancel the scheduled interview?"}
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for cancellation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="activity-cancel-reason">Cancel reason</Label>
            <Textarea
              id="activity-cancel-reason"
              value={cancelReasonDraft}
              onChange={(event) => {
                setCancelReasonDraft(event.target.value);
                if (cancelReasonError) setCancelReasonError("");
              }}
              placeholder="Why are you cancelling this interview?"
              rows={4}
              maxLength={500}
              disabled={cancelBusy}
            />
            {cancelReasonError ? (
              <p className="text-xs text-rose-600">{cancelReasonError}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCancelReasonOpen(false)}
              disabled={cancelBusy}
            >
              Keep interview
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={submitCancelBookingWithReason}
              disabled={cancelBusy}
            >
              {cancelBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Cancel interview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={rescheduleOpen}
        onOpenChange={(value) => {
          if (!value && !rescheduleBusy) {
            setRescheduleOpen(false);
            setRescheduleError("");
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reschedule Interview</DialogTitle>
            <DialogDescription>
              {isReschedulePending && rescheduleSuggestedSlots.length
                ? "Select the shared availability window, then pick the final interview start and end time inside it."
                : "Pick a date, start time, and end time for the new interview window."}{" "}
              Updated emails are sent and the old join link is retired.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {rescheduleError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {rescheduleError}
              </div>
            ) : null}

            <InterviewRescheduleSlotEditor
              timezone={rescheduleTimezone}
              minDate={todayDateValue}
              draft={rescheduleDraft}
              currentSlot={rescheduleCurrentSlot}
              onDraftChange={(nextDraft) => {
                setRescheduleDraft(nextDraft);
              }}
              suggestedSlots={rescheduleSuggestedSlots}
              selectedSuggestionId={selectedSuggestionId}
              onSelectSuggestedSlot={applySuggestedRescheduleSlot}
              availabilityMode={isReschedulePending && rescheduleSuggestedSlots.length > 0}
              availabilityActorLabel={pendingRescheduleActorName}
              disabled={rescheduleBusy}
            />

            <div className="space-y-2">
              <Label>Timezone</Label>
              <Select
                value={rescheduleTimezone}
                onValueChange={setRescheduleTimezone}
                disabled={rescheduleBusy}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  {MEETING_TIMEZONE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setRescheduleOpen(false);
                setRescheduleError("");
              }}
              disabled={rescheduleBusy}
            >
              Keep Current Booking
            </Button>
            <Button
              type="button"
              onClick={handleRescheduleSubmit}
              disabled={rescheduleBusy}
              className="bg-emerald-700 text-white hover:bg-emerald-800"
            >
              {rescheduleBusy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Rescheduling...
                </>
              ) : (
                "Schedule New Time"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EmailDeliveryDialog
        open={emailDeliveryOpen}
        onOpenChange={setEmailDeliveryOpen}
        emailDelivery={emailDelivery}
      />

      <RecordingsDialog
        open={recordingsOpen}
        onOpenChange={setRecordingsOpen}
        recordings={recordings}
        recordingBusyId={recordingBusyId}
        onDownload={handleRecordingDownload}
      />

      <TimelineEventDetailDialog
        open={timelineEventOpen}
        onOpenChange={(value) => {
          setTimelineEventOpen(value);
          if (!value) setSelectedTimelineEvent(null);
        }}
        event={selectedTimelineEvent}
        timezone={scheduleInfo.timezoneLabel || resolvedTimezone}
        booking={effectiveBooking}
      />
    </>
  );

  if (embedded) {
    // Mirror MeetingCreateDialog's embedded gutter (px-6 pt-5) so all three
    // tabs in HumanInterviewHub line up perfectly when switching.
    return (
      <>
        <div className="px-6 pb-6 pt-5">{mainPanel}</div>
        {overlayDialogs}
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(value) => !value && onClose?.()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {mainPanel}
        </DialogContent>
      </Dialog>
      {overlayDialogs}
    </>
  );
};

export default InterviewRoomDialog;
